'use client'

import React from 'react'

import { IconChevronRight } from '@/shared/assets'
import {
  LeftSelectRow,
  ListHeaderTitleText,
  Text,
} from '@/shared/design-system/components'
import { Space } from '@/shared/ui'

import type { ITermItem } from '../model'
interface IPaymentTermsProps {
  termsState: {
    isAllAgreed: boolean
    paymentConfirmation: boolean
    paymentServiceTerms: boolean
    privacyPolicy: boolean
    cancellationPolicy: boolean
  }
  termItems: ITermItem[]
  onToggleTerm: (termKey: ITermItem['key']) => void
  onToggleAll: () => void
  onTermDetailClick: (termType: string) => void
  onNoticeClick: () => void
}

const PaymentTerms = ({
  termsState,
  termItems,
  onToggleTerm,
  onToggleAll,
  onTermDetailClick,
  onNoticeClick,
}: IPaymentTermsProps) => {
  // 약관 상태를 key로 가져오기
  const getTermState = (key: ITermItem['key']): boolean => {
    return termsState[key]
  }

  return (
    <section aria-labelledby='payment-terms-heading' className='flex flex-col'>
      {/* 헤더 */}
      <header
        id='payment-terms-heading'
        className='px-dimension-x5 pb-dimension-x3 pt-dimension-x5'
      >
        <ListHeaderTitleText
          title='유의사항 및 약관동의'
          typography='text-title-1-semibold'
        />
      </header>

      {/* 유의사항 필독 카드 */}
      <div className='flex items-center justify-center px-dimension-x5'>
        <button
          onClick={onNoticeClick}
          className='flex h-dimension-x12 w-full items-center justify-between rounded-dimension-x4 bg-background-neutral-weak px-dimension-x5 py-dimension-x3 transition-opacity hover:opacity-80 active:opacity-60 cursor-pointer'
          aria-label='유의사항 전체보기'
        >
          <div className='flex items-center gap-dimension-x1-5'>
            <Text
              variant='body-1-semibold'
              color='text-foreground-brand-strong'
            >
              유의사항 필독
            </Text>
            <Text
              variant='body-1-medium'
              color='text-foreground-neutral-alternative'
              className='w-[168px]'
            >
              현장 서비스 및 시공 방식 안내
            </Text>
          </div>
          <IconChevronRight
            width={20}
            height={20}
            className='text-foreground-neutral-alternative'
          />
        </button>
      </div>

      <Space h={12} />

      {/* 약관 선택 리스트 */}
      <div className='flex flex-col'>
        {/* 전체 동의하기 */}
        <LeftSelectRow
          type='checkbox'
          checked={termsState.isAllAgreed}
          onChange={onToggleAll}
          fillMaxWidth
          title={
            <Text
              variant='title-2-semibold'
              color='text-foreground-neutral-strong'
            >
              전체 동의하기
            </Text>
          }
        />

        {/* 개별 약관 항목들 */}
        {termItems.map(item => (
          <LeftSelectRow
            fillMaxWidth
            key={item.key}
            id={`payment-term-${item.key}`}
            type='checkmark'
            checked={getTermState(item.key)}
            onChange={() => onToggleTerm(item.key)}
            onRightClick={
              item.hasChevron && item.termType
                ? () => onTermDetailClick(item.termType!)
                : undefined
            }
            title={
              <Text
                variant='body-1-medium'
                color='text-foreground-neutral-normal'
              >
                {item.title}
              </Text>
            }
            rightText={
              item.hasChevron && (
                <IconChevronRight
                  width={20}
                  height={20}
                  className='text-foreground-neutral-alternative'
                />
              )
            }
          />
        ))}
      </div>
    </section>
  )
}

export default PaymentTerms
